// UserActionsMenu — the single source of admin actions against a user.
//
// Used in two places:
//   - the Users table row's 3-dot button
//   - the UserDetail page header
//
// Owns the popover menu UI + every confirmation modal (Suspend,
// Unsuspend, Change plan). The caller supplies the current `user` +
// an `onChange(updated)` callback we fire after every successful
// mutation; the caller is responsible for replacing its local state.
//
// View profile is a navigation link rather than an action — the caller
// can pass `onViewProfile` to surface it, or omit when already on the
// detail page (where the menu sits in the header).

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAction, useMutation, useQuery } from 'convex/react';

import { adminSession, type UserRow } from '@/lib/api';
import { api } from '../../../convex/functions/_generated/api';
import type { Id } from '../../../convex/functions/_generated/dataModel';
import { friendlyError } from '@/lib/errors';
import { EmailConfirmModal, PlainConfirmModal } from './Modals';
import { PlanPopover, choiceToBody, type PlanChoice } from './PlanPopover';

type Modal =
  | null
  | { kind: 'plan'; anchor: HTMLElement }
  | { kind: 'suspend' }
  | { kind: 'unsuspend' }
  | { kind: 'purge' }
  | { kind: 'purgeSynthetic' };

export function UserActionsMenu({
  user,
  onChange,
  onError,
  onViewProfile,
  onPurged,
  trigger,
}: {
  user: UserRow;
  onChange: (updated: UserRow) => void;
  onError: (msg: string) => void;
  /** Optional — when provided, the menu shows a "View profile" item. */
  onViewProfile?: () => void;
  /** Optional — fired after a successful dev-only purge (the user row is
   *  gone). Callers use it to navigate away (detail page); the reactive
   *  list drops the row on its own, so the list omits it. */
  onPurged?: () => void;
  /** Render-prop for the trigger button so callers can style it
   *  (small 3-dot in a table row vs. larger pill in a page header). */
  trigger: (props: { onClick: (anchor: HTMLElement) => void; isOpen: boolean }) => React.ReactNode;
}) {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [modal, setModal] = useState<Modal>(null);
  const [saving, setSaving] = useState(false);

  const setTierMut = useMutation(api.admin.users.setUserTier);
  const setStatusMut = useMutation(api.admin.users.setUserStatus);
  const purgeAct = useAction(api.admin.users.purgeUser);
  const purgeSyntheticAct = useAction(api.admin.users.purgeSyntheticUser);

  // Gate for the Purge item: `allowPurge` from discoveryConfig is true on any
  // non-prod deployment, or on prod when ALLOW_PROD_PURGE is set. The backend
  // action re-checks and refuses otherwise, so this is purely visibility.
  // (Convex dedupes the identical query across every row's menu instance.)
  const token = adminSession.getToken() ?? '';
  const envConfig = useQuery(
    api.admin.flags.discoveryConfig,
    token ? { token } : 'skip',
  );
  const canPurge = envConfig?.allowPurge === true;

  /**
   * Synthetic accounts get their OWN delete path, and it is not gated on
   * allowPurge — cleanup has to work on prod, or seeded test accounts pile
   * up there with no way to remove them. The narrowing that makes that safe
   * is that only synthetic rows are reachable at all.
   *
   * Visibility here is creator-or-superadmin. It is NOT the authority: the
   * backend re-runs the same _purgeAuthority check the API uses, and will
   * also refuse a moderator lacking the synthetic:purge scope, which the
   * session doesn't carry. Same posture as `canPurge` above — the menu
   * hides what is obviously unavailable, the server decides.
   */
  const me = adminSession.getMe();
  const canPurgeSynthetic =
    !!user.synthetic &&
    (me?.role === 'admin' || me?.id === user.synthetic.created_by_admin_id);

  const closeMenu = useCallback(() => setMenuAnchor(null), []);
  const closeModal = useCallback(() => setModal(null), []);

  // ── mutations (reactive: useQuery on caller side re-renders on success;
  //    onChange is fired so the legacy caller-supplied toast/patch still
  //    runs, and onError reports failures) ────────────────────────────
  const applyPlanChoice = useCallback(async (choice: PlanChoice) => {
    setMenuAnchor(null);
    setModal(null);
    setSaving(true);
    const body = choiceToBody(choice);
    const token = adminSession.getToken() ?? '';
    try {
      await setTierMut({
        token,
        userId: user.id as Id<'users'>,
        tier: body.tier,
        plan: body.plan,
      });
      onChange({ ...user, tier: body.tier, plan: body.plan });
    } catch (err) {
      onError(friendlyError(err, 'setUserTier'));
    } finally {
      setSaving(false);
    }
  }, [user, onChange, onError, setTierMut]);

  const applyStatus = useCallback(async (status: 'active' | 'suspended') => {
    setMenuAnchor(null);
    setModal(null);
    setSaving(true);
    const token = adminSession.getToken() ?? '';
    try {
      await setStatusMut({
        token,
        userId: user.id as Id<'users'>,
        status,
      });
      onChange({ ...user, status });
    } catch (err) {
      onError(friendlyError(err, 'setUserStatus'));
    } finally {
      setSaving(false);
    }
  }, [user, onChange, onError, setStatusMut]);

  const applyPurge = useCallback(async () => {
    setMenuAnchor(null);
    setModal(null);
    setSaving(true);
    const t = adminSession.getToken() ?? '';
    try {
      await purgeAct({ token: t, userId: user.id as Id<'users'> });
      onPurged?.();
    } catch (err) {
      onError(friendlyError(err, 'purgeUser'));
    } finally {
      setSaving(false);
    }
  }, [user, onError, onPurged, purgeAct]);

  const applyPurgeSynthetic = useCallback(async () => {
    setMenuAnchor(null);
    setModal(null);
    setSaving(true);
    const t = adminSession.getToken() ?? '';
    try {
      await purgeSyntheticAct({ token: t, userId: user.id as Id<'users'> });
      onPurged?.();
    } catch (err) {
      onError(friendlyError(err, 'purgeSyntheticUser'));
    } finally {
      setSaving(false);
    }
  }, [user, onError, onPurged, purgeSyntheticAct]);

  return (
    <>
      {trigger({
        onClick: (anchor) => setMenuAnchor(anchor),
        isOpen: menuAnchor !== null,
      })}

      {menuAnchor && (
        <RowMenu
          anchor={menuAnchor}
          user={user}
          onCancel={closeMenu}
          onChangePlan={(anchor) => {
            setMenuAnchor(null);
            setModal({ kind: 'plan', anchor });
          }}
          onToggleStatus={() => {
            setMenuAnchor(null);
            setModal({ kind: user.status === 'suspended' ? 'unsuspend' : 'suspend' });
          }}
          onViewProfile={onViewProfile ? () => {
            setMenuAnchor(null);
            onViewProfile();
          } : undefined}
          onPurge={canPurge && !user.synthetic ? () => {
            setMenuAnchor(null);
            setModal({ kind: 'purge' });
          } : undefined}
          onPurgeSynthetic={canPurgeSynthetic ? () => {
            setMenuAnchor(null);
            setModal({ kind: 'purgeSynthetic' });
          } : undefined}
          saving={saving}
        />
      )}

      {modal?.kind === 'plan' && (
        <PlanPopover
          anchor={modal.anchor}
          user={user}
          onCancel={closeModal}
          onChoose={(c) => void applyPlanChoice(c)}
        />
      )}

      <EmailConfirmModal
        open={modal?.kind === 'suspend'}
        title="Suspend this account?"
        body={
          <>
            Existing sessions for <strong>{user.email}</strong> will stop working at the next request. Their public profile will return 404, and ratings + reports against it will be rejected. Reversible — you can unsuspend at any time.
          </>
        }
        expectedEmail={user.email}
        confirmLabel="Suspend"
        confirmStyle="danger"
        onCancel={closeModal}
        onConfirm={() => void applyStatus('suspended')}
      />

      <PlainConfirmModal
        open={modal?.kind === 'unsuspend'}
        title="Unsuspend this account?"
        body={
          <>
            <strong>{user.email}</strong> will be able to sign in again and their public profile will resume rendering.
          </>
        }
        confirmLabel="Unsuspend"
        confirmStyle="primary"
        onCancel={closeModal}
        onConfirm={() => void applyStatus('active')}
      />

      <EmailConfirmModal
        open={modal?.kind === 'purgeSynthetic'}
        title="Delete this synthetic account?"
        body={
          <>
            <strong>{user.email}</strong> was created through the admin API.
            This permanently deletes the account, its profile and all of its
            media — immediately and irreversibly, on this deployment. Real
            signups are unaffected by this action and cannot be deleted here.
          </>
        }
        expectedEmail={user.email}
        confirmLabel="Delete"
        confirmStyle="danger"
        onCancel={closeModal}
        onConfirm={() => void applyPurgeSynthetic()}
      />

      <EmailConfirmModal
        open={modal?.kind === 'purge'}
        title="Purge this account?"
        body={
          <>
            Permanently delete <strong>{user.email}</strong> and <strong>all</strong> their data — profile, media, ratings, DMs, notifications, auth rows, and R2 storage. Bypasses the 30-day soft-delete grace and is <strong>not reversible</strong>. Enabled on non-prod, or on prod while <code>ALLOW_PROD_PURGE</code> is set.
            {' '}This is <strong>{envConfig?.environment === 'prod' ? 'PRODUCTION' : 'development'}</strong> data.
          </>
        }
        expectedEmail={user.email}
        confirmLabel="Purge permanently"
        confirmStyle="danger"
        onCancel={closeModal}
        onConfirm={() => void applyPurge()}
      />
    </>
  );
}

// ── menu (portalled popover anchored to the trigger button) ─────────────

function RowMenu({
  anchor, user, onCancel, onChangePlan, onToggleStatus, onViewProfile, onPurge,
  onPurgeSynthetic, saving,
}: {
  anchor: HTMLElement;
  user: UserRow;
  onCancel: () => void;
  onChangePlan: (anchor: HTMLElement) => void;
  onToggleStatus: () => void;
  onViewProfile?: () => void;
  onPurge?: () => void;
  onPurgeSynthetic?: () => void;
  saving: boolean;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const planItemRef = useRef<HTMLButtonElement | null>(null);

  useLayoutEffect(() => {
    const update = () => {
      const r = anchor.getBoundingClientRect();
      const popW = 240;
      // Item count = base 2 + optional view-profile + whichever delete
      // item applies (they are mutually exclusive: a row is synthetic or
      // it isn't).
      const popH =
        50 +
        (onViewProfile ? 1 : 0) * 40 +
        2 * 40 +
        (onPurge || onPurgeSynthetic ? 1 : 0) * 40;
      let left = r.right - popW;
      let top = r.bottom + 6;
      // Flip up if it would spill off the bottom.
      if (top + popH > window.innerHeight - 8) {
        top = Math.max(8, r.top - popH - 6);
      }
      if (left < 8) left = 8;
      if (left + popW > window.innerWidth - 8) left = window.innerWidth - 8 - popW;
      setPos({ top, left });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [anchor, onViewProfile, onPurge, onPurgeSynthetic]);

  useEffect(() => {
    const onPointerDown = (e: MouseEvent) => {
      const t = e.target as Node | null;
      if (!t) return;
      if (ref.current?.contains(t)) return;
      if (anchor.contains(t)) return;
      onCancel();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [anchor, onCancel]);

  if (!pos) return null;

  const isSuspended = user.status === 'suspended';

  return createPortal(
    <div
      ref={ref}
      role="menu"
      style={{
        position: 'fixed',
        top: pos.top,
        left: pos.left,
        width: 240,
        background: 'var(--surface)',
        border: '1px solid var(--line-strong)',
        borderRadius: 12,
        boxShadow: '0 16px 40px rgba(0,0,0,0.18)',
        padding: 6,
        zIndex: 1000,
        opacity: saving ? 0.6 : 1,
        pointerEvents: saving ? 'none' : 'auto',
      }}
    >
      {onViewProfile && (
        <button type="button" onClick={onViewProfile} style={menuItemStyle(true)}>
          View profile →
        </button>
      )}
      <button
        ref={planItemRef}
        type="button"
        onClick={() => planItemRef.current && onChangePlan(planItemRef.current)}
        title="Change this user’s plan"
        style={menuItemStyle(true)}
      >
        <span>Change plan</span>
      </button>
      {/* Suspend / unsuspend live below a divider — they're the most
          impactful actions on the menu so they're slightly separated. */}
      <div style={{ borderTop: '1px solid var(--line)', margin: '4px 0' }} />
      <button type="button" onClick={onToggleStatus} style={menuItemStyle(true, isSuspended ? undefined : 'danger')}>
        {isSuspended ? 'Unsuspend account' : 'Suspend account'}
      </button>
      {/* Permanent purge. Gated on discoveryConfig.allowPurge upstream, so
          this item renders on non-prod, or on prod while ALLOW_PROD_PURGE is
          set (the backend re-checks regardless). */}
      {onPurge && (
        <button type="button" onClick={onPurge} style={menuItemStyle(true, 'danger')}>
          Purge account
        </button>
      )}
      {onPurgeSynthetic && (
        <button type="button" onClick={onPurgeSynthetic} style={menuItemStyle(true, 'danger')}>
          Delete synthetic account
        </button>
      )}
    </div>,
    document.body,
  );
}

function menuItemStyle(enabled: boolean, tone?: 'danger'): React.CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    padding: '9px 12px',
    background: 'transparent',
    border: 'none',
    borderRadius: 8,
    textAlign: 'left',
    cursor: enabled ? 'pointer' : 'not-allowed',
    color: !enabled ? 'var(--ink-3)' : (tone === 'danger' ? '#a8331f' : 'var(--ink)'),
    fontSize: 13,
    fontWeight: 500,
    fontFamily: 'var(--font-ui)',
    opacity: enabled ? 1 : 0.55,
  };
}
